import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { createSafeHtml } from '../utils/sanitize';
import Footer from './Footer';
import Navbar from './Navbar';
import TopBar from './TopBar';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [pageno, setPageno] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const externalApiUrl =
    process.env.REACT_APP_EXTERNAL_API_URL || 'http://localhost:5000/api';

  const fetchPosts = async page => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${externalApiUrl}/articles`, {
        params: { page: page },
        timeout: 5000,
      });
      setPosts(response.data.posts || []);
      setTotalPages(response.data.total_pages || 1);
      setPageno(response.data.current_page || page);
    } catch (err) {
      console.error('Error fetching posts:', err);
      // Check if we should use mock/fallback data
      if (process.env.REACT_APP_USE_MOCK_BLOG === 'true') {
        // Return empty mock data gracefully
        setPosts([]);
        setTotalPages(1);
        setPageno(1);
        toast.info('Blog posts not available (using mock mode)');
      } else {
        const errorMsg = 'Failed to load posts. Please try again later.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(pageno);
  }, [pageno]);

  const handlePageChange = page => {
    if (page >= 1 && page <= totalPages) {
      setPageno(page);
    }
  };

  if (loading) {
    return (
      <>
        <TopBar />
        <main className="page-blog">
          <div className="container">
            <Navbar />
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: '400px' }}
                >
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar />
        <main className="page-blog">
          <div className="container">
            <Navbar />
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <div className="alert alert-danger mt-4" role="alert">
                  <h4 className="alert-heading">Error Loading Posts</h4>
                  <p>{error}</p>
                  <button className="btn btn-primary" onClick={() => fetchPosts(pageno)}>
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page-blog">
        <div className="container">
          <Navbar />
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              <section className="page-header">
                <h1>Blog</h1>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb foi-breadcrumb">
                    <li className="breadcrumb-item">
                      <a href="/">Home</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Blog
                    </li>
                  </ol>
                </nav>
              </section>

              <section className="foi-page-section pt-0 page-blog-shell">
                {posts.length === 0 ? (
                  <div className="alert alert-info">No posts available.</div>
                ) : (
                  <div className="row">
                    {posts.map(post => (
                      <div className="col-md-6" key={post.pid}>
                        <Link className="blog-post media" to={`/blogd/${post.pid}`}>
                          <div className="blog-post-thumbnail-wrapper">
                            <img
                              src={`${externalApiUrl}/postimages/${post.PostImage}`}
                              alt={post.posttitle}
                              width="155px"
                              className="blog-post-thumbnail mr-md-3"
                            />
                            <span className="blog-post-badge">{post.subcategory}</span>
                          </div>
                          <div className="media-body">
                            <p className="blog-post-date">{post.postingdate}</p>
                            <h5 className="blog-post-title">{post.posttitle}</h5>
                            <p
                              className="blog-post-excerpt"
                              dangerouslySetInnerHTML={createSafeHtml(
                                post.postdetails?.substring(0, 100) + '...' || ''
                              )}
                            ></p>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="row">
                    <div className="col-12">
                      <nav>
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${pageno === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageno - 1)}
                              disabled={pageno === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${pageno === i + 1 ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${pageno === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageno + 1)}
                              disabled={pageno === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Blog;
